import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { getCompanyLogo } from 'lib/api/company';
import { expressGetCompanyLogo } from 'lib/api/express';
import { Surplus } from 'lib/api/reports/types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import DocumentSection from './components/DocumentSection';
import PaymentSection from './components/PaymentSection';
import SurplusReleasedSection from './components/SurplusReleasedSection';
import SurplusRetainedSection from './components/SurplusRetainedSection';
import { generateSurplusReportPDF } from './generateSurplusReportPDF';

interface SurplusDetailsProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  surplus: Surplus | null;
}

const SurplusDetails = (props: SurplusDetailsProps) => {
  const { open, setOpen, surplus } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!surplus) return;
    setExporting(true);
    try {
      const imageBase64 = user?.express
        ? await expressGetCompanyLogo(dispatch, user.clientId as string)
        : await getCompanyLogo(dispatch);
      await generateSurplusReportPDF(surplus, user?.name || '', imageBase64);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  if (!surplus) return null;
  return (
    <>
      <Dialog
        maxWidth="lg"
        fullWidth
        open={open}
        onClose={() => setOpen(false)}
        scroll="paper"
      >
        <Box
          sx={{
            p: 3,
            position: 'relative',
            maxHeight: '80vh',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pr: 6,
            }}
          >
            <Typography variant="h3">Detalle excedente</Typography>
            <Button
              variant="text"
              onClick={() => void handleExport()}
              disabled={exporting}
              sx={{
                color: 'primary.main',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
              }}
            >
              {exporting ? 'Exportando...' : 'Exportar a PDF'}
            </Button>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            size="large"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <DocumentSection surplus={surplus} />
            <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
              <Box sx={{ flex: 2 }}>
                <SurplusRetainedSection surplus={surplus} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <SurplusReleasedSection surplus={surplus} />
              </Box>
            </Box>
            <PaymentSection surplus={surplus} />
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default SurplusDetails;
